import {
  useQuery,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
} from "react-query";
import supabase from "./supabase";

// React Query client
const client = new QueryClient();

/**** USERS ****/

// Fetch user data
// Note: This is called automatically in `auth.js` and data is merged into `auth.user`
export function useUser(uid) {
  // Manage data fetching with React Query: https://react-query.tanstack.com/overview
  return useQuery(
    // Unique query key: https://react-query.tanstack.com/guides/query-keys
    ["user", { uid }],
    // Query function that fetches data
    () =>
      supabase
        .from("users")
        .select(`*, customers ( * )`)
        .eq("id", uid)
        .single()
        .then(handle),
    // Only call query function if we have a `uid`
    { enabled: !!uid }
  );
}

// Fetch user data (non-hook)
// Useful if you need to fetch data from outside of a component
export function getUser(uid) {
  return supabase
    .from("users")
    .select(`*, customers ( * )`)
    .eq("id", uid)
    .single()
    .then(handle);
}

// Update an existing user
export async function updateUser(uid, data) {
  const response = await supabase
    .from("users")
    .update(data)
    .eq("id", uid)
    .then(handle);
  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(["user", { uid }]);
  return response;
}

/**** ITEMS ****/
/* Example query functions (modify to your needs) */

// Fetch item data
export function useItem(id) {
  return useQuery(
    ["item", { id }],
    () => supabase.from("items").select().eq("id", id).single().then(handle),
    { enabled: !!id }
  );
}

// Fetch all items by owner
export function useItemsByOwner(owner) {
  return useQuery(
    ["items", { owner }],
    () =>
      supabase
        .from("items")
        .select()
        .eq("owner", owner)
        .order("createdAt", { ascending: false })
        .then(handle),
    { enabled: !!owner }
  );
}

// Create a new item
export async function createItem(data) {
  const response = await supabase.from("items").insert([data]).then(handle);
  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(["items"]);
  return response;
}

// Update an item
export async function updateItem(id, data) {
  const response = await supabase
    .from("items")
    .update(data)
    .eq("id", id)
    .then(handle);
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(["item", { id }]),
    client.invalidateQueries(["items"]),
  ]);
  return response;
}

// Delete an item
export async function deleteItem(id) {
  const response = await supabase
    .from("items")
    .delete()
    .eq("id", id)
    .then(handle);
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(["item", { id }]),
    client.invalidateQueries(["items"]),
  ]);
  return response;
}

// 1. Create a prompt
export async function createPrompt(data) {
  const response = await supabase
  .from("entity__prompt")
  .insert([data])
  .select()
  .then(handle);
  
  // Optionally invalidate a query if we have a "usePromptsByOwner"
  await client.invalidateQueries(["prompts"]);
  
  return response;
}

export async function fetchLambdaModels() {
  const { data, error } = await supabase
    .from("entity__lambda_models")
    .select("id, model_name")
    .eq("is_live", true)
    .order("model_name", { ascending: true });

  if (error) throw error;
  return data;
}

// 2. If you want to fetch prompts by owner
export function usePromptsByOwner(owner) {
  return useQuery(
    ["prompts", { owner }],
    () =>
      supabase
        .from("entity__prompt")
        .select("*")
        .eq("owner", owner)
        .order("createdAt", { ascending: false })
        .then(handle),
    { enabled: !!owner }
  );
}

// Fetch all variables by owner
export function useVariablesByOwner(owner) {
  return useQuery(
    ["variables", { owner }],
    () =>
      supabase
        .from("entity__variable")
        .select("*")
        .eq("owner", owner)
        .order("createdAt", { ascending: false })
        .then(handle),
    { enabled: !!owner }
  );
}

// Create a new variable
export async function createVariable(data) {
  // data might look like { owner, title, content, shorthand }
  const response = await supabase
    .from("entity__variable")
    .insert([data])
    .select()
    .then(handle);

  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(["variables"]);
  return response;
}

/***************************************************************************
 *  SEQUENCE
 ***************************************************************************/

// Fetch all sequences by owner
export function useSequencesByOwner(owner) {
  return useQuery(
    ["sequences", { owner }],
    () =>
      supabase
        .from("entity__sequence")
        .select("*")
        .eq("owner", owner)
        .order("createdAt", { ascending: false })
        .then(handle),
    { enabled: !!owner }
  );
}

// Create a new sequence
export async function createSequence(data) {
  // data might look like { owner, prompts } 
  // prompts is an array of prompt IDs (like ["bf0f9a14-b664-4fbd-8e12-..."]).
  const response = await supabase
    .from("entity__sequence")
    .insert([data])
    .select()
    .then(handle);

  // Invalidate sequences
  await client.invalidateQueries(["sequences"]);
  return response;
}

export async function upsertTagsForEntity({ entityType, entityId, tags }) {
  for (const name of tags) {
    // Upsert tag and obtain its id in one round‑trip
    const { data: tag, error: upsertErr } = await supabase
      .from("taxonomy")
      .upsert(
        { name, type: "tag" },                     // data
        { onConflict: "name,type",                 // use the unique index
          ignoreDuplicates: false,
          returning: "representation" }            // so we get the row back
      )
      .select()
      .single();
    if (upsertErr) throw upsertErr;

    // Bridge table: ignore duplicates gracefully
    const { error: bridgeErr } = await supabase
      .from("entity__taxonomy")
      .upsert(
        { entity_type: entityType, entity_id: entityId, taxonomy_id: tag.id },
        { onConflict: "entity_type,entity_id,taxonomy_id", ignoreDuplicates: true }
      );
    if (bridgeErr) throw bridgeErr;
  }
}

export function useEntityTaxonomyByOwner(entityType, owner) {
  return useQuery(
    [entityType + "_tags", { owner }],
    async () => {
      // 1. fetch ids owned by the user
      const { data: entities, error: eErr } = await supabase
        .from(`entity__${entityType}`)
        .select("id")
        .eq("owner", owner);
      if (eErr) throw eErr;

      const ids = entities.map((e) => e.id);
      if (ids.length === 0) return [];

      // 2. now pull the tags
      const { data, error } = await supabase
        .from("entity__taxonomy")
        .select("entity_id, taxonomy_id, taxonomy ( name )")
        .eq("entity_type", entityType)
        .in("entity_id", ids);
      if (error) throw error;

      return data;
    },
    { enabled: !!owner }
  );
}

/**** HELPERS ****/

// Get response data or throw error if there is one
function handle(response) {
  if (response.error) throw response.error;
  return response.data;
}

// React Query context provider that wraps our app
export function QueryClientProvider(props) {
  return (
    <QueryClientProviderBase client={client}>
      {props.children}
    </QueryClientProviderBase>
  );
}